import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderState } from '@/components/form-builder/FormBuilder';

interface FormSettingsPanelProps {
  formState: FormBuilderState;
  onUpdate: (updates: Partial<FormBuilderState>) => void;
  onClose: () => void;
}

const FormSettingsPanel: React.FC<FormSettingsPanelProps> = ({
  formState,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<'general' | 'theme' | 'advanced'>('general');

  const handleThemeChange = (key: keyof FormBuilderState['theme'], value: string) => {
    onUpdate({
      theme: {
        ...formState.theme,
        [key]: value,
      },
    });
  };

  const handleConfigChange = (key: keyof FormBuilderState['config'], value: any) => {
    onUpdate({
      config: {
        ...formState.config,
        [key]: value,
      },
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Configurações do Formulário
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-brand-200 dark:border-brand-800">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'general'
              ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-600 dark:border-brand-400'
              : 'text-slate-500 dark:text-slate-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-500/10 dark:hover:text-brand-300'
          }`}
        >
          {t('formBuilder.settings.general', 'Geral')}
        </button>
        <button
          onClick={() => setActiveTab('theme')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'theme'
              ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-600 dark:border-brand-400'
              : 'text-slate-500 dark:text-slate-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-500/10 dark:hover:text-brand-300'
          }`}
        >
          {t('formBuilder.settings.appearance', 'Tema')}
        </button>
        <button
          onClick={() => setActiveTab('advanced')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'advanced'
              ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-600 dark:border-brand-400'
              : 'text-slate-500 dark:text-slate-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-500/10 dark:hover:text-brand-300'
          }`}
        >
          {t('formBuilder.settings.advanced', 'Avançado')}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {activeTab === 'general' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nome do Formulário *
              </label>
              <input
                type="text"
                value={formState.formName}
                onChange={(e) => onUpdate({ formName: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm font-mono"
                placeholder="formulario-contato"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Usado na URL: /form/{formState.formName || 'nome-do-formulario'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descrição do Formulário
              </label>
              <textarea
                value={formState.formDescription}
                onChange={(e) => onUpdate({ formDescription: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
                rows={3}
                placeholder={'Descricao que aparece no topo do formulario'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Texto do Botão
              </label>
              <input
                type="text"
                value={formState.submitButtonText}
                onChange={(e) => onUpdate({ submitButtonText: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
                placeholder="Enviar"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mensagem de Sucesso
              </label>
              <input
                type="text"
                value={formState.successMessage}
                onChange={(e) => onUpdate({ successMessage: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
                placeholder={'Formulario enviado com sucesso'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                URL de Redirecionamento
              </label>
              <input
                type="url"
                value={formState.redirectUrl}
                onChange={(e) => onUpdate({ redirectUrl: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
                placeholder="https://exemplo.com/obrigado"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Opcional. Redireciona após submissão bem-sucedida.
              </p>
            </div>
          </>
        )}

        {activeTab === 'theme' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cor Primária
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={formState.theme.primaryColor}
                  onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                  className="h-10 w-16 rounded border border-gray-300 dark:border-gray-700 cursor-pointer"
                />
                <input
                  type="text"
                  value={formState.theme.primaryColor}
                  onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-mono"
                  placeholder="#e20074"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cor Secundária
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={formState.theme.secondaryColor}
                  onChange={(e) => handleThemeChange('secondaryColor', e.target.value)}
                  className="h-10 w-16 rounded border border-gray-300 dark:border-gray-700 cursor-pointer"
                />
                <input
                  type="text"
                  value={formState.theme.secondaryColor}
                  onChange={(e) => handleThemeChange('secondaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-mono"
                  placeholder="#7f123f"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Família da Fonte
              </label>
              <select
                value={formState.theme.fontFamily}
                onChange={(e) => handleThemeChange('fontFamily', e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
              >
                <option value="inherit">{'Padrao do sistema'}</option>
                <option value="'Inter', sans-serif">Inter</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Lato', sans-serif">Lato</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                CSS Customizado
              </label>
              <textarea
                value={formState.theme.customCSS}
                onChange={(e) => handleThemeChange('customCSS', e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 text-gray-100 border border-gray-700 rounded-lg font-mono text-xs"
                rows={8}
                placeholder=".form-container {
  max-width: 800px;
}

.btn-submit {
  font-size: 18px;
}"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                CSS adicional para personalizar o formulário
              </p>
            </div>

            {/* Color Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Presets de Cores
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { name: 'T-SYSTEMS', primary: '#e20074', secondary: '#7f123f' },
                  { name: 'Magenta', primary: '#c70065', secondary: '#4a0d2a' },
                  { name: 'Plum', primary: '#8a1f4d', secondary: '#3d0b23' },
                  { name: 'Coral', primary: '#f07ab0', secondary: '#8c2f45' },
                  { name: 'Rose', primary: '#ff5fa2', secondary: '#a83264' },
                  { name: 'Vinho', primary: '#6d1039', secondary: '#2a0517' },
                ].map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => {
                      handleThemeChange('primaryColor', preset.primary);
                      handleThemeChange('secondaryColor', preset.secondary);
                    }}
                    className="p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-brand-500 dark:hover:border-brand-400 transition-colors group"
                  >
                    <div className="flex space-x-1 mb-1">
                      <div
                        className="h-6 w-6 rounded"
                        style={{ backgroundColor: preset.primary }}
                      />
                      <div
                        className="h-6 w-6 rounded"
                        style={{ backgroundColor: preset.secondary }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-brand-600 dark:group-hover:text-brand-400">
                      {preset.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'advanced' && (
          <>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Barra de Progresso
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Mostrar progresso do preenchimento
                  </p>
                </div>
                <button
                  onClick={() => handleConfigChange('showProgressBar', !formState.config.showProgressBar)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formState.config.showProgressBar ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formState.config.showProgressBar ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Múltiplas Submissões
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Permitir enviar o formulário várias vezes
                  </p>
                </div>
                <button
                  onClick={() => handleConfigChange('allowMultipleSubmissions', !formState.config.allowMultipleSubmissions)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formState.config.allowMultipleSubmissions ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formState.config.allowMultipleSubmissions ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    CAPTCHA
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Proteção contra spam (em breve)
                  </p>
                </div>
                <button
                  onClick={() => handleConfigChange('enableCaptcha', !formState.config.enableCaptcha)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formState.config.enableCaptcha ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  disabled
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formState.config.enableCaptcha ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Formulário Multi-Etapas
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Dividir em múltiplas páginas (em breve)
                  </p>
                </div>
                <button
                  onClick={() => handleConfigChange('multiStep', !formState.config.multiStep)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formState.config.multiStep ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  disabled
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formState.config.multiStep ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {formState.config.multiStep && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Campos por Etapa
                  </label>
                  <input
                    type="number"
                    value={formState.config.stepsPerPage}
                    onChange={(e) => handleConfigChange('stepsPerPage', parseInt(e.target.value) || 5)}
                    min="1"
                    max="20"
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FormSettingsPanel;

